import { CommandProps, Editor, Extension } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'

export type IndentOptions = {
   /** Node types that can carry an indent level. */
   types: string[]
   /** Pixels per indent level. 40px matches Word's default 1.27cm indent. */
   stepPx: number
   /** Highest allowed indent level. */
   maxLevel: number
}

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      indent: {
         /** Increase the indent level of every selected paragraph. */
         indent: () => ReturnType
         /** Decrease the indent level of every selected paragraph. */
         outdent: () => ReturnType
      }
   }
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

export const Indent = Extension.create<IndentOptions>({
   name: 'indent',

   // Higher priority than ListItem so this extension owns the Tab binding and can
   // decide whether to nest a list item or indent a plain paragraph.
   priority: 1000,

   addOptions() {
      return {
         // Paragraphs are the only block the editor has left: headings were switched off with
         // everything else the toolbar cannot undo (#6465).
         types: ['paragraph'],
         stepPx: 40,
         maxLevel: 8,
      }
   },

   addGlobalAttributes() {
      const { types, stepPx, maxLevel } = this.options

      return [
         {
            types,
            attributes: {
               indent: {
                  default: 0,
                  parseHTML: (element) => {
                     const marginLeft = parseFloat(element.style.marginLeft)
                     if (Number.isNaN(marginLeft) || marginLeft <= 0) {
                        return 0
                     }
                     return clamp(Math.round(marginLeft / stepPx), 0, maxLevel)
                  },
                  renderHTML: (attributes) =>
                     attributes.indent ? { style: `margin-left: ${attributes.indent * stepPx}px` } : {},
               },
            },
         },
      ]
   },

   addCommands() {
      const { types, maxLevel } = this.options

      const setIndent =
         (delta: number) =>
         ({ state, tr, dispatch }: CommandProps) => {
            const { from, to } = state.selection
            let changed = false

            state.doc.nodesBetween(from, to, (node: ProseMirrorNode, pos: number, parent: ProseMirrorNode | null) => {
               if (!types.includes(node.type.name)) {
                  return true
               }
               // Paragraphs inside list items are indented by nesting the list item instead.
               if (parent?.type.name === 'listItem') {
                  return false
               }

               const current: number = node.attrs.indent ?? 0
               const next = clamp(current + delta, 0, maxLevel)

               if (next !== current) {
                  changed = true
                  // Under editor.can() there is no dispatch, so the document is only
                  // inspected — never touched.
                  if (dispatch) {
                     tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next })
                  }
               }

               return false
            })

            return changed
         }

      return {
         indent: () => setIndent(1),
         outdent: () => setIndent(-1),
      }
   },

   addKeyboardShortcuts() {
      return {
         // Always report the key as handled so Tab never moves focus out of the editor,
         // even when there is nothing left to indent.
         Tab: () => {
            increaseIndent(this.editor)
            return true
         },
         'Shift-Tab': () => {
            decreaseIndent(this.editor)
            return true
         },
      }
   },
})

/** Nest the current list item, or indent the selected paragraphs when outside a list. */
export const increaseIndent = (editor: Editor): boolean =>
   editor.isActive('listItem')
      ? editor.chain().focus().sinkListItem('listItem').run()
      : editor.chain().focus().indent().run()

/** Lift the current list item, or outdent the selected paragraphs when outside a list. */
export const decreaseIndent = (editor: Editor): boolean =>
   editor.isActive('listItem')
      ? editor.chain().focus().liftListItem('listItem').run()
      : editor.chain().focus().outdent().run()

export const canIncreaseIndent = (editor: Editor): boolean =>
   editor.isActive('listItem') ? editor.can().sinkListItem('listItem') : editor.can().indent()

export const canDecreaseIndent = (editor: Editor): boolean =>
   editor.isActive('listItem') ? editor.can().liftListItem('listItem') : editor.can().outdent()
